/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {MDXWrapper} from 'lib/mdx';
import Image from 'next/image';
import ImageTabs from 'components/image-tabs';
import LanguageLinks from 'components/language-links';
import openAddOn from '../public/how-to/1-open-addon.png';
import allowAccess from '../public/how-to/2-allow-access.png';
import uploadFile from '../public/how-to/3-upload-file.png';
import selectLanguage from '../public/how-to/4-select-language.png';
import preview from '../public/how-to/5-preview.png';
import purchase from '../public/how-to/6-purchase.png';
import buyNow from '../public/how-to/7-buy-now.png';
import afterPurchase from '../public/how-to/8-after-purchase.png';
import transcribing from '../public/how-to/9-transcribing.png';
import showTranscript from '../public/how-to/10-show-transcript.png';
import editorSpeakers from '../public/how-to/editor-speakers.png';
import suggestedFile from '../public/how-to/suggested-file.png';
export const metadata = {
  title: 'How to Transcribe',
  description: 'Transcribe any audio or video file by following this easy step-by-step guide to using the TranscribeKit speech-to-text add-on for Google Drive',
  language: 'en',
  availableLanguages: ['en', 'es', 'fr']
};
const MDXLayout = function Layout({children}) {
  return _jsx(MDXWrapper, {
    metadata: metadata,
    children: children
  });
};
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h1: "h1",
      p: "p",
      em: "em",
      h2: "h2",
      a: "a",
      strong: "strong"
    }, props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h1, {
        children: "How to Transcribe Audio or Video to Text"
      }), "\n", _jsx(LanguageLinks, {
        current: metadata.language,
        available: metadata.availableLanguages
      }), "\n", _jsxs(_components.p, {
        children: ["Automatic speech-to-text uses artificial intelligence to convert recorded speech to words. Follow these steps to convert any audio or video file to text using the ", _jsx(_components.em, {
          children: "TranscribeKit"
        }), " add-on for ", _jsx(_components.em, {
          children: "Google Drive"
        }), "."]
      }), "\n", _jsxs(_components.h2, {
        children: ["1. Install the ", _jsx(_components.em, {
          children: "Google Drive"
        }), " add-on"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "TranscribeKit"
        }), " works as a ", _jsx(_components.em, {
          children: "Google Drive"
        }), " add-on. You can install it by clicking\n", _jsx(_components.a, {
          href: "https://workspace.google.com/marketplace/app/transcribekit/831507088910",
          target: "_blank",
          rel: "nofollow noopener noreferrer",
          children: "here"
        }), " to go to the ", _jsx(_components.em, {
          children: "Google Marketplace"
        }), " and then clicking the ", _jsx(_components.strong, {
          children: "Install"
        }), " button.\nOnce you’ve installed the add-on you’ll need to go to your ", _jsx(_components.a, {
          href: "https://drive.google.com",
          target: "_blank",
          rel: "nofollow noopener noreferrer",
          children: "Google Drive"
        }), " and click the ", _jsx(_components.em, {
          children: "TranscribeKit"
        }), " icon on the side panel on the right to open it."]
      }), "\n", _jsx("div", {
        className: "flex border lg:-mx-24",
        children: _jsx(Image, {
          src: openAddOn,
          alt: "Open the add-on",
          quality: 100,
          placeholder: "blur",
          priority: true
        })
      }), "\n", _jsx(_components.h2, {
        children: "2. Upload your audio or video file"
      }), "\n", _jsxs(_components.p, {
        children: ["With the add-on open, click on any audio or video file in your ", _jsx(_components.em, {
          children: "Google Drive"
        }), ". Do not double-click on the file because then it will start to play. The add-on will detect the file that you’ve selected and an ", _jsx(_components.strong, {
          children: "Allow Access"
        }), " button will appear. Click on it to give ", _jsx(_components.em, {
          children: "TranscribeKit"
        }), " permission to access the file. After that an ", _jsx(_components.strong, {
          children: "Upload File"
        }), " button will appear, click on it to send the file to the transcription server. Wait a bit and then click the ", _jsx(_components.strong, {
          children: "Refresh"
        }), " button and the next screen should be displayed."]
      }), "\n", _jsxs(_components.p, {
        children: ["⚠ ", _jsxs(_components.em, {
          children: ["Clicking on files in the ", _jsx(_components.strong, {
            children: "Suggested"
          }), " section at the top won't work. For those files you need to ", _jsx(_components.strong, {
            children: "right"
          }), "-click and then click ", _jsx(_components.strong, {
            children: "Show file location"
          })]
        })]
      }), "\n", _jsx("div", {
        className: "flex justify-center",
        children: _jsx(ImageTabs, {
          images: [{
            image: suggestedFile,
            caption: 'Suggested File'
          }, {
            image: allowAccess,
            caption: 'Allow Access'
          }, {
            image: uploadFile,
            caption: 'Upload File'
          }]
        })
      }), "\n", _jsx(_components.h2, {
        children: "3. Select the language of the speech in the recording"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your file is uploaded, the ", _jsx(_components.strong, {
          children: "New Transcript"
        }), " screen will appear with the ", _jsx(_components.strong, {
          children: "Recording Language"
        }), " set to ", _jsx(_components.em, {
          children: "English (United States)"
        }), ". If the language spoken in your file is not English then please select the correct language from the list."]
      }), "\n", _jsx("div", {
        className: "flex justify-center",
        children: _jsx("div", {
          className: "flex border w-max",
          children: _jsx(Image, {
            src: selectLanguage,
            alt: "Select recording language",
            quality: 100,
            placeholder: "blur"
          })
        })
      }), "\n", _jsx(_components.h2, {
        children: "4. Preview the first few seconds"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you have selected the correct language, click on the ", _jsx(_components.strong, {
          children: "Click Here to Preview"
        }), " button. This will transcribe the first 15 seconds of your file."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "If the preview is blank it could be because there is no speech at the beginning of your recording. The add-on will try to ignore silence but this will not work if there are sounds in the background. You can still go ahead and transcribe the file but please double-check that you have selected the correct language."
        })
      }), "\n", _jsx("div", {
        className: "flex justify-center",
        children: _jsx("div", {
          className: "flex border w-max",
          children: _jsx(Image, {
            src: preview,
            alt: "Preview the transcript",
            quality: 100,
            placeholder: "blur"
          })
        })
      }), "\n", _jsx(_components.h2, {
        children: "5. Purchase transcription time"
      }), "\n", _jsxs(_components.p, {
        children: ["If your transcription time balance is too low then ", _jsxs(_components.strong, {
          children: ["\"Click HERE to purchase more transcription time at ", _jsx(_components.em, {
            children: "TranscribeKit.com"
          }), "\""]
        }), " will be displayed. Click on that link to open your account page on ", _jsx(_components.em, {
          children: "TranscribeKit.com"
        }), " in a new tab."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once the ", _jsx(_components.em, {
          children: "Account"
        }), " page loads click on any of the ", _jsx(_components.strong, {
          children: "Buy Now"
        }), " buttons to purchase either 1, 5 or 10 hours of transcription time using your credit card or ", _jsx(_components.em, {
          children: "PayPal"
        }), " account. Payments are processed by ", _jsx(_components.em, {
          children: "Paddle"
        }), " and you will receive an invoice by email."]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["If you want to purchase, for example, two hours you can click on the ", _jsx(_components.strong, {
            children: "Buy Now"
          }), " button for 1 hour and update the quantity in the top left section of the checkout screen."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once the payment is successful the transaction should automatically appear on your ", _jsx(_components.em, {
          children: "Account"
        }), " page (this may take a few seconds)."]
      }), "\n", _jsx("div", {
        className: "flex justify-center",
        children: _jsx(ImageTabs, {
          images: [{
            image: purchase,
            caption: 'Balance too low'
          }, {
            image: buyNow,
            caption: 'Account page'
          }]
        })
      }), "\n", _jsx(_components.h2, {
        children: "6. Request the full transcript"
      }), "\n", _jsxs(_components.p, {
        children: ["Now that you have transcription time you can return to ", _jsx(_components.em, {
          children: "Google Drive"
        }), " and click the ", _jsx(_components.strong, {
          children: "Refresh"
        }), " button to update your balance. After that, click on ", _jsx(_components.strong, {
          children: "Click Here to Transcribe"
        }), " and your request will be sent to the transcription server."]
      }), "\n", _jsxs(_components.p, {
        children: ["⚠ ", _jsxs(_components.em, {
          children: ["Transcription time will be subtracted from your balance so please double-check that you have selected the correct ", _jsx(_components.strong, {
            children: "Recording Language"
          }), "."]
        })]
      }), "\n", _jsx("div", {
        className: "flex justify-center",
        children: _jsx("div", {
          className: "flex border w-max",
          children: _jsx(Image, {
            src: afterPurchase,
            alt: "New transcript",
            quality: 100,
            placeholder: "blur"
          })
        })
      }), "\n", _jsx(_components.h2, {
        children: "7. Wait for the email containing the transcript"
      }), "\n", _jsx(_components.p, {
        children: "A screen will appear to let you know that your file is being transcribed. This should take less than an hour and you’ll receive an email once it’s complete. Note that the time it takes depends on the length of your file and how busy the server is so it may sometimes take longer."
      }), "\n", _jsxs(_components.p, {
        children: ["The email you receive will have an ", _jsx(_components.strong, {
          children: "Open"
        }), " button which will take you to the full transcript. This can also be accessed by viewing the transcript in the add-on and then clicking the ", _jsx(_components.strong, {
          children: "Open Full Transcript"
        }), " button. If you accidentally transcribed using the wrong language then you can click the ", _jsx(_components.strong, {
          children: "Transcribe Again"
        }), " button in the add-on to try again. Note that this will subtract more transcription time from your balance."]
      }), "\n", _jsx("div", {
        className: "flex justify-center",
        children: _jsx(ImageTabs, {
          images: [{
            image: transcribing,
            caption: 'Transcribing'
          }, {
            image: showTranscript,
            caption: 'Show Transcript'
          }]
        })
      }), "\n", _jsx(_components.h2, {
        children: "8. Make corrections and export"
      }), "\n", _jsxs(_components.p, {
        children: ["After opening the full transcript you can play back the audio, make corrections to the text and split or join paragraphs. You can also set the speaker for each paragraph (select ", _jsx(_components.strong, {
          children: "Show speakers"
        }), " first)."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Please note that only two speakers will be detected. If your audio contains more speakers then you can manually set them."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After making your changes click ", _jsx(_components.strong, {
          children: "Google Docs"
        }), " then ", _jsx(_components.strong, {
          children: "Export new Google Doc"
        }), " to create a ", _jsx(_components.em, {
          children: "Google Doc"
        }), " containing the full transcript. Exports are saved to the ", _jsx(_components.em, {
          children: "TranscribeKit Transcripts"
        }), " folder in your ", _jsx(_components.em, {
          children: "Google Drive"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you need to view or revert to a previous version of your transcript then click on the ", _jsx(_components.strong, {
          children: "Previous versions"
        }), " link."]
      }), "\n", _jsx("div", {
        className: "flex justify-center",
        children: _jsx("div", {
          className: "flex border w-max",
          children: _jsx(Image, {
            src: editorSpeakers,
            alt: "Transcript editor",
            quality: 100,
            placeholder: "blur"
          })
        })
      }), "\n", _jsx(_components.h2, {
        children: "Related Articles"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.a, {
          href: "/how-to-transcribe-zoom",
          children: "How to Transcribe a Zoom Meeting Recording"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.a, {
          href: "/how-to-transcribe-google-meet",
          children: "How to Transcribe a Google Meet Recording"
        })
      })]
    });
  }
}
export default MDXContent;
